import { H3, P } from "@/components/Typography";
import { Trans, useTranslation } from "react-i18next";
import { faCopy } from "@fortawesome/pro-duotone-svg-icons";
import {
  useGetShopifyIntegration,
  useGetWidgetCode,
} from "@/requests/hooks/collect.ts";
import Shimmer from "@/components/Shimmer";
import { useEffect, useState } from "react";
import { useTracking } from "@/requests/hooks/app.ts";
import {
  MAILTO_SUPPORT,
  SHOPIFY_CHECKOUT_SETTINGS_URL,
} from "@/utils/const.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Accordion from "@/components/Accordion";
import { Button } from "@/components/Button";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { toast } from "react-toastify";
import { tv } from "tailwind-variants";
import OnboardingVideo from "@/components/OnboardingVideo";

import additionalScriptsMp4 from "@/assets/videos/collect/onboarding/add-additional-scripts-code.mp4";
import addAllExtensionsMp4 from "@/assets/videos/collect/onboarding/add-all-extensions.mp4";
import addAdditionalScriptsAndCheckoutInformationMp4 from "@/assets/videos/collect/onboarding/add-additional-scripts-code-and-checkout-extension.mp4";
import addThankYouPageMp4 from "@/assets/videos/collect/onboarding/add-order-status-and-thank-you-extensions.mp4";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { OnboardingProgression } from "@/components/OnboardingProgression";

const styles = tv({
  slots: {
    orderedList:
      "mt-10 flex list-outside list-decimal flex-col gap-10 ps-20 text-start",
    unorderedList: "list-inside list-[lower-alpha]",
    ctaContainer: "w-full sm:w-[unset]",
  },
});

const EmailInstallation = () => {
  const { t } = useTranslation();
  const { mutate: sendTracking } = useTracking();
  const {
    isLoading: isShopifyIntegrationLoading,
    data: shopifyIntegrationData,
  } = useGetShopifyIntegration();
  const { isLoading: isWidgetCodeLoading, data: widgetCodeData } =
    useGetWidgetCode();
  const {
    onboardingStatus: { isOnboarding },
  } = useOnboardingStatus();
  const [copyToClipboardText, setCopyToClipboardText] = useState<string>(
    t("settings.widgetSettings.installation.widget.copyToClipboard"),
  );

  const { orderedList, unorderedList } = styles();

  const isShopifyPlus =
    shopifyIntegrationData?.shopifyPlus ||
    shopifyIntegrationData?.checkoutExtensibilityEnabled;
  const isSnippet = !shopifyIntegrationData?.thankYouExtensibilityEnabled;

  useEffect(() => {
    if (
      copyToClipboardText ===
      t("settings.widgetSettings.installation.widget.copiedToClipboard")
    )
      setTimeout(() => {
        setCopyToClipboardText(
          t("settings.widgetSettings.installation.widget.copyToClipboard"),
        );
      }, 1000);
  }, [copyToClipboardText]);

  const handleOnClick = () => {
    setCopyToClipboardText(
      t("settings.widgetSettings.installation.widget.copiedToClipboard"),
    );
    navigator.clipboard.writeText(widgetCodeData?.script ?? "");
    toast.success(
      t("settings.widgetSettings.installation.widget.notification"),
    );
  };

  useEffect(() => {
    sendTracking(
      "control-panel.collect.widget-settings.email-installation.view",
    );
  }, []);

  const renderHeading = () => (
    <div className="max-w-md">
      <div className="mb-10">
        <H3 className="font-bold">
          {t("settings.widgetSettings.installation.header.widget.title")}
        </H3>
      </div>
      <P>
        {t("settings.widgetSettings.installation.header.widget.description")}
      </P>
    </div>
  );

  const renderWidgetCodeSection = () => {
    if (isWidgetCodeLoading) return <Shimmer height="118px" width="100%" />;

    return (
      <div
        className="mt-30 flex min-h-70 w-full grow cursor-pointer flex-col break-all rounded-sm bg-gray-light"
        onClick={handleOnClick}
        data-testid="code-container">
        <div className="flex max-w-[220px] flex-row items-center justify-around rounded-tl-sm bg-gradient-to-r from-primary to-green px-30 py-10 text-mobile-small uppercase text-white">
          {copyToClipboardText}
          <FontAwesomeIcon icon={faCopy} color="white" />
        </div>
        <div className="p-20 pt-10 font-mono text-mobile-small leading-8">
          {widgetCodeData?.script}
        </div>
      </div>
    );
  };

  const renderSupportSection = () => (
    <div className="mt-40 flex flex-row items-center gap-10">
      {t("settings.widgetSettings.installation.supportSection")}
      <Button
        as="a"
        variant="link"
        width="unset"
        href={MAILTO_SUPPORT}
        icon={faCaretRight}
        iconPosition="end"
        iconClassName="h-15">
        {t("components.cta.needHelp")}
      </Button>
    </div>
  );

  const renderAdditionalScriptsInstructions = () => {
    if (!isSnippet) return null;

    return (
      <div>
        <li>
          <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step1">
            one
            <strong>two</strong>
            three
          </Trans>
          <ul className={unorderedList()}>
            <li>
              <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step2">
                one
                <strong>two</strong>
              </Trans>
            </li>
            <li>
              <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step3">
                one
                <Button
                  trackingId={{
                    eventName:
                      "onboarding.collect.optimize-checkout.add-widget-code",
                  }}
                  as="a"
                  variant="link"
                  icon={faCaretRight}
                  iconPosition="end"
                  iconClassName="w-15 h-15"
                  href={
                    widgetCodeData?.shopifyCheckoutSettingsUrl ||
                    SHOPIFY_CHECKOUT_SETTINGS_URL
                  }
                  width="unset"
                  testId="snippet-checkout-settings-link">
                  two
                </Button>
              </Trans>
            </li>
            <li>
              <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step4">
                one
                <strong>two</strong>
                three
                <strong>four</strong>
                five
              </Trans>
            </li>
            <li>
              <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step5">
                one
                <strong>two</strong>
              </Trans>
            </li>
          </ul>
        </li>
        {renderWidgetCodeSection()}
      </div>
    );
  };

  const renderLinkToCheckoutSettingsStep = () => {
    if (isSnippet && !isShopifyPlus) return null;

    return (
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.openAdminStep">
          one
          <Button
            trackingId={{
              eventName: "onboarding.collect.optimize-checkout.settings-url",
            }}
            as="a"
            variant="link"
            icon={faCaretRight}
            iconPosition="end"
            iconClassName="w-15 h-15"
            href={
              shopifyIntegrationData?.shopifyCheckoutSettingsUrl ||
              SHOPIFY_CHECKOUT_SETTINGS_URL
            }
            width="unset"
            testId="extensibility-checkout-settings-link">
            two
          </Button>
          three
          <strong>four</strong>
        </Trans>
      </li>
    );
  };

  const renderCheckoutCustomizationStep = () => {
    if (isSnippet && !isShopifyPlus) return null;

    if (isSnippet) {
      return (
        <li>
          <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.openCustomiseStepPostWidget">
            one
            <strong>two</strong>
            three
            <strong>four</strong>
          </Trans>
        </li>
      );
    }

    return (
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.openCustomiseStep">
          one
          <strong>two</strong>
          three
          <strong>four</strong>
        </Trans>
      </li>
    );
  };

  const renderCheckoutInformationInstructions = () => {
    if (!isShopifyPlus) return null;

    return (
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1">
          one
          <strong>two</strong>
          three
        </Trans>
        <ul className={unorderedList()}>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1a">
              one
              <strong>two</strong>
              three
              <strong>four</strong>
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1b">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1c">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1d">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
        </ul>
      </li>
    );
  };

  const renderThankYouInstructions = () => {
    if (isSnippet) return null;

    return (
      <li>
        <Trans
          i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1"
          values={{ page: "Thank You" }}>
          one
          <strong>two</strong>
          three
        </Trans>
        <ul className={unorderedList()}>
          <li>
            <Trans
              i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1a"
              values={{ page: "Thank You" }}>
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1b">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1c">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1d">
              one
              <strong>two</strong>
              three
              <strong>four</strong>
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1e">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1f">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
        </ul>
      </li>
    );
  };

  const getVideoUrl = () => {
    // Snippet only
    if (isSnippet && !isShopifyPlus) return additionalScriptsMp4;
    // CI & Snippet
    if (isSnippet && isShopifyPlus)
      return addAdditionalScriptsAndCheckoutInformationMp4;
    // TY Only
    if (!isSnippet && !isShopifyPlus) return addThankYouPageMp4;
    // CI & TY
    return addAllExtensionsMp4;
  };

  const renderInstructions = () => {
    return (
      <Accordion
        defaultIsExpanded={true}
        topContent={
          <strong>
            {t(
              "settings.widgetSettings.installation.header.widget.instructions.heading",
            )}
          </strong>
        }>
        <div className="flex flex-row gap-30">
          <div className="flex-grow">
            <ol className={orderedList()}>
              {renderAdditionalScriptsInstructions()}
              {renderLinkToCheckoutSettingsStep()}
              {renderCheckoutCustomizationStep()}
              {renderCheckoutInformationInstructions()}
              {renderThankYouInstructions()}
            </ol>
          </div>
          <div className="flex-shrink-0">
            <div className="w-[400px]">
              <OnboardingVideo videoLocation={getVideoUrl()} />
            </div>
          </div>
        </div>
      </Accordion>
    );
  };

  const renderLoadingOrInstructions = () => {
    if (isWidgetCodeLoading || isShopifyIntegrationLoading) {
      return <Shimmer />;
    }

    return renderInstructions();
  };

  const renderOnboardingProgression = () =>
    !!isOnboarding && (
      <div className="mb-10 mt-30">
        <OnboardingProgression
          currentStep="CONFIGURE_DATASHIPS_APP"
          enabled={true}
        />
      </div>
    );

  return (
    <div className="flex flex-col gap-20">
      {renderHeading()}
      {renderLoadingOrInstructions()}
      {renderOnboardingProgression()}
      {renderSupportSection()}
    </div>
  );
};

export default EmailInstallation;
